import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import { sendInquiry } from "../services/api/inquiryApi";

export default function ContactSection({ playError, playMoney }) {
  const [cooldown, setCooldown] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const startCooldown = useCallback(() => {
    setCooldown(true);
    setCooldownTime(60);

    const interval = setInterval(() => {
      setCooldownTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCooldown(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const getCooldownText = (seconds) => {
    if (seconds > 50) return `🚀 Inquiry Sent! ${seconds}s`;
    if (seconds > 40) return `✨ We're on it! ${seconds}s`;
    if (seconds > 30) return `⚡ Processing your vision... ${seconds}s`;
    if (seconds > 20) return `🔥 Sparking possibilities... ${seconds}s`;
    if (seconds > 10) return `💡 Almost ready again... ${seconds}s`;
    return `✅ Ready in ${seconds}s`;
  };

  const showSuccess = () => {
    Swal.fire({
      title: "🎉 Thank You!",
      html: `
        <p style="font-size: 1.1rem; line-height: 1.6; margin-bottom: 15px;">
          We're excited to hear from you! Your inquiry has been received successfully.
        </p>
        <p style="font-size: 1rem; color: #b8c5d6;">
          Our team will review your request and get back to you within <strong style="color: #00d4ff;">24 hours</strong>.
        </p>
        <p style="font-size: 0.95rem; color: #b8c5d6; margin-top: 15px;">
          We can't wait to bring your vision to life! 🚀
        </p>
      `,
      icon: "success",
      confirmButtonText: "Awesome!",
      confirmButtonColor: "#00d4ff",
      background: "rgba(21, 27, 46, 0.95)",
      color: "#ffffff",
      backdrop: "rgba(0, 0, 0, 0.7)",
      customClass: {
        popup: "swal-custom-popup",
        title: "swal-custom-title",
        confirmButton: "swal-custom-button",
      },
    });
  };

  const showError = (message) => {
    Swal.fire({
      title: "Oops!",
      text: message || "Something went wrong. Please try again.",
      icon: "error",
      confirmButtonText: "Try Again",
      confirmButtonColor: "#ff6b35",
      background: "rgba(21, 27, 46, 0.95)",
      color: "#ffffff",
      backdrop: "rgba(0, 0, 0, 0.7)",
      customClass: {
        popup: "swal-custom-popup",
        title: "swal-custom-title",
        confirmButton: "swal-custom-button",
      },
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.target;

    if (cooldown || submitting) return;

    if (!form.checkValidity()) {
      playError();
      form.reportValidity();
      return;
    }

    // Collect form data
    const payload = {
      name: form.name.value.trim(),
      email: form.email.value.trim(),
      company: form.company.value.trim(),
      budget: form.budget.value,
      message: form.message.value.trim(),
      website: form.website ? form.website.value : "", // honeypot
    };

    try {
      setSubmitting(true);
      await sendInquiry(payload);

      if (playMoney) playMoney();
      showSuccess();
      form.reset();
      startCooldown();
    } catch (err) {
      playError();
      showError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleButtonClick = (e) => {
    e.stopPropagation(); // Prevent click sound from App wrapper
  };

  const handleButtonMouseDown = (e) => {
    e.stopPropagation(); // Stop the mousedown event that triggers click sound
  };

  // Play a subtle click sound when focusing on form fields
  const handleFieldFocus = (e) => {
    e.stopPropagation();

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const audioContext = new AudioCtx();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = "sine";

    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.1);
  };

  // Prevent click sound on mousedown for form fields
  const handleFieldMouseDown = (e) => {
    e.stopPropagation();
  };

  const buttonDisabled = cooldown || submitting;

  const buttonLabel = submitting
    ? "Sending..."
    : cooldown
    ? getCooldownText(cooldownTime)
    : "Send Inquiry";

  return (
    <section className="contact" id="contact" data-aos="fade-up">
      <motion.div
        className="contact-inner"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <h2>Let's Build Something Together</h2>
        <p className="contact-intro">
          Tell us about your idea or the problem you're trying to solve. We'll get back to you within 24 hours with clear next steps.
        </p>

        <form className="contact-form" onSubmit={handleFormSubmit} noValidate>
          <div className="field-row">
            <motion.div className="field" whileFocus={{ scale: 1.02 }}>
              <label>Name</label>
              <input
                type="text"
                name="name"
                placeholder="Your name"
                required
                minLength={2}
                maxLength={100}
                onFocus={handleFieldFocus}
                onMouseDown={handleFieldMouseDown}
              />
            </motion.div>
            <motion.div className="field" whileFocus={{ scale: 1.02 }}>
              <label>Email</label>
              <input
                type="email"
                name="email"
                placeholder="you@example.com"
                required
                onFocus={handleFieldFocus}
                onMouseDown={handleFieldMouseDown}
              />
            </motion.div>
          </div>

          <div className="field-row">
            <div className="field">
              <label>Company / Organization</label>
              <input
                type="text"
                name="company"
                placeholder="Your company or startup name"
                onFocus={handleFieldFocus}
                onMouseDown={handleFieldMouseDown}
              />
            </div>
            <div className="field">
              <label>Budget Range</label>
              <select
                name="budget"
                defaultValue=""
                onFocus={handleFieldFocus}
                onMouseDown={handleFieldMouseDown}
              >
                <option value="" disabled>
                  Select budget range
                </option>
                <option value="Under 1L">Under ₹1 Lakh</option>
                <option value="1L - 5L">₹1 Lakh – ₹5 Lakh</option>
                <option value="5L - 15L">₹5 Lakh – ₹15 Lakh</option>
                <option value="15L+">₹15 Lakh+</option>
                <option value="Not sure">Not sure yet</option>
              </select>
            </div>
          </div>

          <div className="field">
            <label>Tell us about your project</label>
            <textarea
              name="message"
              rows="4"
              placeholder="Describe your idea, product, or the problem you want to solve..."
              required
              minLength={10}
              maxLength={2000}
              onFocus={handleFieldFocus}
              onMouseDown={handleFieldMouseDown}
            />
          </div>

          {/* Honeypot field - hidden from real users, catches bots */}
          <input
            type="text"
            name="website"
            tabIndex="-1"
            autoComplete="off"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              opacity: 0,
            }}
            aria-hidden="true"
          />

          <motion.button
            type="submit"
            className="contact-button"
            disabled={buttonDisabled}
            onClick={handleButtonClick}
            onMouseDown={handleButtonMouseDown}
            whileHover={
              !buttonDisabled
                ? {
                    scale: 1.05,
                    boxShadow: "0 10px 30px rgba(0, 212, 255, 0.5)",
                  }
                : {}
            }
            whileTap={!buttonDisabled ? { scale: 0.95 } : {}}
            transition={{ type: "spring", stiffness: 400 }}
            style={buttonDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
          >
            {buttonLabel}
          </motion.button>
        </form>
      </motion.div>
    </section>
  );
}
